import WindowedSelect, { components, OptionProps } from 'react-windowed-select'
import { classNames } from 'utils'
import { GNewWindowedSelectProps } from './types'
import { GoCheck } from 'react-icons/go'
import { FiChevronDown } from 'react-icons/fi'
export const GNewWindowedSelect = <T,>(props: GNewWindowedSelectProps<T>) => {
	const {
		label,
		subtitle,
		error,
		placeholder = 'Select',
		options,
		value,
		getOptionValue,
		getOptionLabel,
		labelClassName,
		optionColor = 'var(--t-coupon)',
		borderRadius = '0.375rem',
		isCurrencySelect = false,
		optionSelectColor = 'var(--t-google-btn)',
		menuStyle,
		getOptionLabelComponent,
		...rest
	} = props

	// Inline react-select styles bypass the Tailwind splits (bg-t-smoke -> --bg-smoke,
	// text-t-base-blue -> --base-blue-text), so the flipping vars go in directly.
	// The parents (--t-smoke, --base-blue) are frozen white/navy and vanish in dark.
	const colourStyles = {
		container: (styles: any) => {
			return {
				...styles
			}
		},
		placeholder: (styles: any) => ({
			...styles,
			backgroundColor: 'transparent',
			fontSize: '0.875rem'
		}),
		control: (styles: any, { isHovered, isFocused }: any) => ({
			...styles,
			borderRadius: borderRadius,
			color: 'var(--t-coupon)',
			fontWeight: 'medium',
			fontFamily: 'Host Grotesk',
			border: isCurrencySelect ? '1px solid var(--t-plan-btn)' : '',
			fontSize: '0.875rem',
			'--tw-shadow': '0 1px 2px 0 rgb(0 0 0 / 0.05)',
			'--tw-shadow-colored': '0 1px 2px 0 var(--tw-shadow-color)',
			...(isHovered
				? {
						borderColor: !isCurrencySelect ? 'var(--t-gray-input)' : ''
				  }
				: {
						backgroundColor: 'var(--bg-smoke)'
				  }),
			...(isFocused
				? {
						outline: !isCurrencySelect ? '2px solid transparent' : '',
						outlineOffset: !isCurrencySelect ? '2px' : '',
						'--tw-ring-inset': 'var(--tw-empty,/*!*/ /*!*/)',
						'--tw-ring-offset-width': '0px',
						'--tw-ring-offset-color': 'var(--background)',
						'--tw-ring-color': 'var(--bg-smoke)',
						'--tw-ring-offset-shadow':
							'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)',
						'--tw-ring-shadow':
							'var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color)',
						boxShadow:
							'var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow)',
						borderColor: !isCurrencySelect ? 'var(--t-gray-input)' : '',
						backgroundColor: 'var(--bg-smoke)'
				  }
				: {
						boxShadow:
							'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow)',
						borderColor: 'var(--t-gray-input)'
				  })
		}),
		dropdownIndicator: (styles: any) => ({
			...styles,
			color: 'var(--base-blue-text)',
			paddingRight: '0.5rem',
			':hover': {
				color: 'var(--base-blue-text)'
			}
		}),
		menu: (provided: any) => ({
			...provided,
			backgroundColor: 'var(--bg-popover)',
			...menuStyle,
			zIndex: '9999'
		}),
		indicatorSeparator: (styles: any) => ({
			...styles,
			backgroundColor: 'var(--background)'
		}),
		input: (styles: any) => {
			const isSmallScreen =
				typeof window !== 'undefined' && window.innerWidth < 640
			return {
				...styles,
				color: 'var(--t-coupon)',
				padding: isSmallScreen ? '7px' : '0px'
			}
		},
		option: (styles: any, { isSelected, isFocused }: any) => ({
			...styles,
			backgroundColor: isSelected
				? optionSelectColor
				: isFocused
				? ''
				: 'default',
			color: optionColor,
			fontFamily: 'Host Grotesk',
			fontWeight: 'medium',
			width: isCurrencySelect ? '100%' : '98%',
			fontSize: '0.875rem',
			margin: '0 auto',
			borderRadius: !isCurrencySelect ? '5px' : '0'
		}),
		singleValue: (styles: any) => ({
			...styles,
			color: 'var(--t-coupon)',
			fontSize: '0.875rem'
		})
	}

	const DropdownIndicator = (props: any) => {
		return (
			<components.DropdownIndicator {...props}>
				<FiChevronDown width={20} height={20} />
			</components.DropdownIndicator>
		)
	}

	const Option = (props: OptionProps<any>) => {
		const { isSelected, data } = props
		return (
			<components.Option {...props}>
				<div
					className={`flex items-center ${
						isSelected && 'flex-row-reverse'
					} justify-between`}
				>
					{isSelected && <GoCheck className="text-t-ad-border" />}
					{getOptionLabelComponent ? (
						getOptionLabelComponent(data)
					) : (
						<span>
							{getOptionLabel
								? getOptionLabel(data)
								: data.account_number ?? data.label}
						</span>
					)}
				</div>
			</components.Option>
		)
	}

	return (
		<div>
			{label && (
				<div
					className={`${labelClassName} block text-base font-normal text-t-google-ad `}
				>
					{label}
				</div>
			)}
			{subtitle && <p className="text-sm mt-1 text-t-google-ad">{subtitle}</p>}
			<div
				className={classNames(
					'relative windowed-select',
					label || subtitle ? 'mt-2' : ''
				)}
			>
			<WindowedSelect
					placeholder={<span className="w-full truncate">{placeholder}</span>}
					components={{ DropdownIndicator, Option }}
					isSearchable={true}
					value={value}
					options={options?.map((opt: any) => {
						return {
							...opt,
							value: getOptionValue ? getOptionValue(opt) : opt,
							label: getOptionLabel ? getOptionLabel(opt) : opt
						}
					})}
					windowThreshold={0}
					styles={colourStyles}
					{...(rest || {})}
				/>
			</div>
			{error && (
				<p className="mt-1 text-sm text-red-700" id="email-description">
					{error}
				</p>
			)}
		</div>
	)
}
